import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Sparkles, Plus, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownView } from '@/components/app/MarkdownView';
import { useCurrentUser } from '@/lib/auth';
import {
  useCourses, useMyCourses, useCourse, useCreateCourse, useGenerateCourse,
  usePublishCourse, useAssignCourse, useCompleteCourse,
  type CourseListItem, type MyCourse,
} from '@/lib/learning';

export function LearningPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const courses = useCourses();
  const mine = useMyCourses();
  const [take, setTake] = useState<string | null>(null);
  const [creating, setCreating] = useState<null | 'ai' | 'manual'>(null);

  const isManager = ['owner', 'admin', 'manager'].includes(me.data?.role ?? '');
  const todo = (mine.data ?? []).filter((c) => !c.completedAt);
  const done = (mine.data ?? []).filter((c) => c.completedAt);

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('learning.title', { defaultValue: 'Learning' })}</h1>
            <p className="mt-1 text-sm text-ink-500">{t('learning.subtitle', { defaultValue: '学習 · assigned courses + AI-authored micro-courses from your policies.' })}</p>
          </div>
          {isManager && (
            <div className="flex gap-2">
              <button onClick={() => setCreating('ai')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition">
                <Sparkles className="h-4 w-4" />{t('learning.generate', { defaultValue: 'AI course' })}
              </button>
              <button onClick={() => setCreating('manual')} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 text-ink-800 text-sm font-semibold hover:bg-ink-50 transition">
                <Plus className="h-4 w-4" />{t('learning.manual', { defaultValue: 'Manual' })}
              </button>
            </div>
          )}
        </div>

        {/* My courses */}
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-700">{t('learning.myCourses', { defaultValue: 'My courses' })}</h2>
          {(mine.data ?? []).length === 0 && <p className="mt-2 text-sm text-ink-500">{t('learning.noneAssigned', { defaultValue: 'No courses assigned to you.' })}</p>}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[...todo, ...done].map((c) => <MyCourseCard key={c.id} course={c} onOpen={() => setTake(c.id)} />)}
          </div>
        </section>

        {/* Manage */}
        {isManager && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold text-ink-700">{t('learning.allCourses', { defaultValue: 'All courses' })}</h2>
            <div className="mt-3 rounded-2xl border border-ink-200 bg-white overflow-hidden">
              {courses.isLoading && <p className="p-4 text-sm text-ink-500">{t('common.loading')}</p>}
              {courses.data?.length === 0 && <p className="p-4 text-sm text-ink-500">{t('learning.empty', { defaultValue: 'No courses yet.' })}</p>}
              <ul className="divide-y divide-ink-100">
                {courses.data?.map((c) => <ManageRow key={c.id} course={c} onPreview={() => setTake(c.id)} />)}
              </ul>
            </div>
          </section>
        )}
      </div>

      {take && <CourseViewer id={take} onClose={() => setTake(null)} />}
      {creating === 'ai' && <GenerateDialog onClose={() => setCreating(null)} />}
      {creating === 'manual' && <ManualDialog onClose={() => setCreating(null)} />}
    </AppShell>
  );
}

function MyCourseCard({ course: c, onOpen }: { course: MyCourse; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <button onClick={onOpen} className="text-start rounded-2xl border border-ink-200 bg-white p-4 hover:border-accent-300 hover:shadow-sm transition">
      <div className="flex items-center gap-2">
        <GraduationCap className="h-4 w-4 text-accent-600" />
        {c.source === 'ai' && <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-600">AI</span>}
        {c.completedAt
          ? <span className={`ms-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${c.passed ? 'bg-emerald-50 text-emerald-700' : 'bg-warn-50 text-warn-700'}`}>{c.passed ? t('learning.passed', { defaultValue: 'Passed' }) : t('learning.failed', { defaultValue: 'Retry' })} · {c.scorePct}%</span>
          : <span className="ms-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{t('learning.todo', { defaultValue: 'To do' })}</span>}
      </div>
      <p className="mt-1 font-semibold text-ink-900">{c.title}</p>
      {c.description && <p className="text-xs text-ink-500 line-clamp-2">{c.description}</p>}
    </button>
  );
}

function ManageRow({ course: c, onPreview }: { course: CourseListItem; onPreview: () => void }) {
  const { t } = useTranslation();
  const publish = usePublishCourse();
  const assign = useAssignCourse();
  const [msg, setMsg] = useState<string | null>(null);
  const rate = c.assigned > 0 ? Math.round((c.completed / c.assigned) * 100) : 0;

  async function doAssign() {
    const r = await assign.mutateAsync(c.id);
    setMsg(t('learning.assignedMsg', { count: r.assigned, defaultValue: 'Assigned to {{count}}.' }));
  }

  return (
    <li className="px-4 py-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[12rem]">
        <div className="flex items-center gap-2">
          {c.source === 'ai' && <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-600">AI</span>}
          <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${c.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{t(`learning.status_${c.status}`, { defaultValue: c.status })}</span>
        </div>
        <button onClick={onPreview} className="font-semibold text-ink-900 hover:text-accent-600 hover:underline text-start">{c.title}</button>
        <p className="num text-xs text-ink-500">{c.completed}/{c.assigned} {t('learning.completedWord', { defaultValue: 'completed' })}{c.assigned > 0 && ` · ${rate}%`} · {t('learning.pass', { defaultValue: 'pass' })} {c.passPct}%</p>
      </div>
      {c.status === 'draft' && <button onClick={() => publish.mutate(c.id)} disabled={publish.isPending} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800">{t('learning.publish', { defaultValue: 'Publish' })}</button>}
      {c.status === 'published' && <button onClick={doAssign} disabled={assign.isPending} className="text-xs font-semibold text-accent-700 hover:text-accent-600">{t('learning.assignAll', { defaultValue: 'Assign to all' })}</button>}
      {msg && <span className="text-xs text-emerald-600">{msg}</span>}
    </li>
  );
}

function CourseViewer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const course = useCourse(id);
  const complete = useCompleteCourse();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<{ scorePct: number; passed: boolean; passPct: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const c = course.data;

  async function submit() {
    setError(null);
    try {
      const payload = (c?.questions ?? []).map((q) => ({ questionId: q.id, choiceIndex: answers[q.id] ?? -1 }));
      const r = await complete.mutateAsync({ id, answers: payload });
      setResult(r);
    } catch (e: unknown) {
      setError(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-2xl w-full p-6 max-h-[88vh] overflow-y-auto">
        {!c ? <p className="text-sm text-ink-500">{t('common.loading')}</p> : (
          <>
            <h2 className="text-xl font-semibold text-ink-900">{c.title}</h2>
            {c.description && <p className="mt-1 text-sm text-ink-500">{c.description}</p>}

            {c.lessons.map((l) => (
              <section key={l.id} className="mt-5">
                <h3 className="text-base font-semibold text-ink-800">{l.title}</h3>
                <MarkdownView>{l.body}</MarkdownView>
              </section>
            ))}

            {c.questions.length > 0 && !result && (
              <section className="mt-6 border-t border-ink-100 pt-4">
                <h3 className="text-sm font-semibold text-ink-700">{t('learning.quiz', { defaultValue: 'Quiz' })} · {t('learning.passNeeded', { pct: c.passPct, defaultValue: '{{pct}}% to pass' })}</h3>
                <div className="mt-3 space-y-4">
                  {c.questions.map((q, qi) => (
                    <div key={q.id}>
                      <p className="text-sm font-medium text-ink-800">{qi + 1}. {q.prompt}</p>
                      <div className="mt-1.5 space-y-1">
                        {q.choices.map((ch, ci) => (
                          <button key={ci} onClick={() => setAnswers({ ...answers, [q.id]: ci })} className="flex items-center gap-2 w-full text-start text-sm px-2 py-1.5 rounded-lg hover:bg-ink-50 transition">
                            {answers[q.id] === ci ? <CheckCircle2 className="h-4 w-4 text-accent-600 shrink-0" /> : <Circle className="h-4 w-4 text-ink-300 shrink-0" />}
                            <span className={answers[q.id] === ci ? 'text-ink-900' : 'text-ink-600'}>{ch}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
                <div className="flex justify-end gap-2 pt-4">
                  <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg">{t('common.cancel')}</button>
                  <button onClick={submit} disabled={complete.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{complete.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('learning.submitQuiz', { defaultValue: 'Submit & complete' })}</button>
                </div>
              </section>
            )}

            {c.questions.length === 0 && !result && (
              <div className="flex justify-end gap-2 pt-5 border-t border-ink-100 mt-5">
                <button onClick={submit} disabled={complete.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{t('learning.markComplete', { defaultValue: 'Mark complete' })}</button>
              </div>
            )}

            {result && (
              <div className={`mt-6 rounded-xl border p-4 text-center ${result.passed ? 'border-emerald-200 bg-emerald-50' : 'border-warn-500/30 bg-warn-50'}`}>
                <p className={`text-2xl font-bold ${result.passed ? 'text-emerald-700' : 'text-warn-700'}`}>{result.scorePct}%</p>
                <p className="text-sm font-semibold mt-1">{result.passed ? t('learning.youPassed', { defaultValue: 'Course complete — passed!' }) : t('learning.youFailed', { pct: result.passPct, defaultValue: 'Below the {{pct}}% pass mark — review and retake.' })}</p>
                <button onClick={onClose} className="mt-3 px-4 py-2 rounded-lg bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800">{t('common.done', { defaultValue: 'Done' })}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DialogShell({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-xl w-full p-6 max-h-[85vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function GenerateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const gen = useGenerateCourse();
  const [topic, setTopic] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [passPct, setPassPct] = useState(70);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try { await gen.mutateAsync({ topic: topic.trim(), sourceText: sourceText.trim() || undefined, passPct }); onClose(); }
    catch (e: unknown) { setError(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message); }
  }

  return (
    <DialogShell onClose={onClose} title={t('learning.genTitle', { defaultValue: 'Generate a micro-course with AI' })}>
      <label className="block">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('learning.topic', { defaultValue: 'Topic' })}</span>
        <input value={topic} onChange={(e) => setTopic(e.target.value)} className="input" placeholder={t('learning.topicPlaceholder', { defaultValue: 'e.g. Information security basics for new hires' })} />
      </label>
      <label className="block mt-4">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('learning.sourceText', { defaultValue: 'Paste a policy (optional — grounds the course in your docs)' })}</span>
        <textarea value={sourceText} onChange={(e) => setSourceText(e.target.value)} rows={5} className="input w-full" placeholder={t('learning.sourcePlaceholder', { defaultValue: 'Paste relevant policy text here…' })} />
      </label>
      <label className="block mt-4 w-32">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('learning.passMark', { defaultValue: 'Pass mark %' })}</span>
        <input type="number" min={0} max={100} value={passPct} onChange={(e) => setPassPct(Number(e.target.value))} className="input" />
      </label>
      <p className="mt-3 text-[11px] text-ink-400">{t('learning.genNote', { defaultValue: 'AI drafts 2–4 lessons + a multiple-choice quiz. Review and publish before assigning.' })}</p>
      {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <div className="flex justify-end gap-2 pt-5">
        <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg">{t('common.cancel')}</button>
        <button onClick={submit} disabled={gen.isPending || topic.trim().length < 3} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{gen.isPending ? t('learning.generating', { defaultValue: 'Drafting…' }) : t('learning.generateBtn', { defaultValue: 'Generate' })}</button>
      </div>
    </DialogShell>
  );
}

function ManualDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateCourse();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lessons, setLessons] = useState<Array<{ title: string; body: string }>>([{ title: '', body: '' }]);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const ls = lessons.map((l) => ({ title: l.title.trim(), body: l.body.trim() })).filter((l) => l.title);
      await create.mutateAsync({ title: title.trim(), description: description.trim() || undefined, lessons: ls });
      onClose();
    } catch (e: unknown) { setError(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message); }
  }

  return (
    <DialogShell onClose={onClose} title={t('learning.manualTitle', { defaultValue: 'New course' })}>
      <label className="block">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('learning.courseTitle', { defaultValue: 'Title' })}</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
      </label>
      <label className="block mt-3">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('learning.description', { defaultValue: 'Description' })}</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="input" />
      </label>
      <div className="mt-4 space-y-3">
        <span className="block text-xs font-medium text-ink-700">{t('learning.lessons', { defaultValue: 'Lessons' })}</span>
        {lessons.map((l, i) => (
          <div key={i} className="rounded-lg border border-ink-200 p-3 space-y-2">
            <div className="flex gap-2">
              <input value={l.title} onChange={(e) => setLessons(lessons.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} className="input flex-1" placeholder={t('learning.lessonTitle', { defaultValue: 'Lesson title' })} />
              {lessons.length > 1 && <button onClick={() => setLessons(lessons.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <textarea value={l.body} onChange={(e) => setLessons(lessons.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} rows={3} className="input w-full" placeholder={t('learning.lessonBody', { defaultValue: 'Lesson content (markdown supported)' })} />
          </div>
        ))}
        <button onClick={() => setLessons([...lessons, { title: '', body: '' }])} className="text-xs font-semibold text-accent-700 hover:text-accent-600">+ {t('learning.addLesson', { defaultValue: 'Add lesson' })}</button>
      </div>
      {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <div className="flex justify-end gap-2 pt-5">
        <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg">{t('common.cancel')}</button>
        <button onClick={submit} disabled={create.isPending || title.trim().length < 2} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{create.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('learning.createBtn', { defaultValue: 'Create' })}</button>
      </div>
    </DialogShell>
  );
}
